/**
 * Single source of truth for per-theme color/font values. Imported unmodified from both
 * tailwind.config.js (Node/build context) and React (Vite client context via theme.ts) --
 * plain ESM JS on purpose, no TypeScript syntax, since tailwind.config.js's loader doesn't
 * transpile TS. Every theme must define the exact same set of keys (espresso/brick/vellum/
 * ink/dust/moss/citation/mint, display/prose/mono/sans/arabic) so a shared primitive
 * component written once (e.g. using `bg-brick`/`font-display`) renders correctly under any
 * theme without itself knowing which one is active.
 *
 * When adding/editing a theme's fontFamily here, update that theme's VITE_GOOGLE_FONTS_QUERY
 * in package.json to match (see client/DESIGN_SYSTEM.md for the loading convention).
 *
 * danger/warning severity colors are intentionally NOT part of this token set -- per
 * DESIGN_SYSTEM.md, they stay Tailwind's plain stock red/amber classes across every theme.
 * moss (the "Open Access" designation) also stays a real green across every theme -- it
 * signals a genuine academic-publishing convention, not decorative brand color, so it isn't
 * themed like brick/espresso/vellum are.
 */

export const THEME_IDS = /** @type {const} */ ([
  'warm-broadsheet',
  'clinical-modern',
  'classic-academic',
  'minimal-scandinavian',
  'field-notes',
]);

export const DEFAULT_THEME_ID = 'warm-broadsheet';

const OPEN_ACCESS_GREEN = {
  DEFAULT: '#2A6B3C',
  light: '#EBF5EE',
};

export const THEMES = {
  'warm-broadsheet': {
    layoutVariant: 'broadsheet',
    colors: {
      espresso: { DEFAULT: '#2C1810', 2: '#4A2E20' },
      // brick.DEFAULT was #C84B31 (~4.4:1 on vellum as text -- just under WCAG
      // AA's 4.5:1 minimum, flagged by the audit); darkened slightly, keeping
      // it distinct from brick.dark (still a lighter/brighter red than that).
      brick: { DEFAULT: '#B84329', dark: '#A83B24', light: '#F5E8E4' },
      vellum: { DEFAULT: '#FBF8F3', 2: '#F2EDE5' },
      // ink-3 was #B09A8E (~2.5:1 on vellum, fails WCAG AA's 4.5:1 minimum for
      // normal text -- flagged by the JBAEM performance/accessibility audit).
      // Darkened to keep the same "muted/tertiary" role while passing (~5:1).
      ink: { DEFAULT: '#3D2B1F', 2: '#7A5C4E', 3: '#7C6955' },
      dust: '#E8DDD5',
      moss: OPEN_ACCESS_GREEN,
      citation: '#44A6B2',
      mint: '#6EE7A0',
    },
    fontFamily: {
      display: ['"Playfair Display"', 'Georgia', 'serif'],
      prose: ['"Libre Baskerville"', 'Georgia', 'serif'],
      mono: ['"Space Mono"', '"Courier New"', 'monospace'],
      sans: ['Inter', 'system-ui', 'sans-serif'],
      arabic: ['Alexandria', 'sans-serif'],
    },
  },

  'clinical-modern': {
    layoutVariant: 'clinical',
    colors: {
      espresso: { DEFAULT: '#1E293B', 2: '#334155' },
      brick: { DEFAULT: '#3B6FD4', dark: '#2D5AB8', light: '#EDF1F7' },
      vellum: { DEFAULT: '#F7F9FC', 2: '#EDF1F7' },
      ink: { DEFAULT: '#1E293B', 2: '#475569', 3: '#94A3B8' },
      dust: '#DCE3ED',
      moss: OPEN_ACCESS_GREEN,
      citation: '#2563EB',
      mint: '#7DD3FC',
    },
    fontFamily: {
      display: ['Inter', 'system-ui', 'sans-serif'],
      prose: ['Inter', 'system-ui', 'sans-serif'],
      mono: ['"IBM Plex Mono"', '"Courier New"', 'monospace'],
      sans: ['Inter', 'system-ui', 'sans-serif'],
      arabic: ['Alexandria', 'sans-serif'],
    },
  },

  'classic-academic': {
    layoutVariant: 'academic',
    colors: {
      // "Navy, burgundy, cream. Rule dividers instead of cards. Leans into gravitas and
      // print tradition." -- gold doubles as the citation/footnote-marker accent since the
      // shared token vocabulary has no dedicated slot for a fifth brand color.
      espresso: { DEFAULT: '#1B2A4A', 2: '#2E4569' },
      brick: { DEFAULT: '#7A2331', dark: '#5C1A25', light: '#F0E1E4' },
      vellum: { DEFAULT: '#F5F1E6', 2: '#EDE5D2' },
      ink: { DEFAULT: '#2B3A52', 2: '#5A6B85', 3: '#A79B7E' },
      dust: '#D9D0BA',
      moss: OPEN_ACCESS_GREEN,
      citation: '#A6875A',
      mint: '#8FA37E',
    },
    fontFamily: {
      display: ['Georgia', '"Times New Roman"', 'serif'],
      prose: ['Georgia', '"Times New Roman"', 'serif'],
      mono: ['"Courier Prime"', '"Courier New"', 'monospace'],
      sans: ['"PT Sans"', 'system-ui', 'sans-serif'],
      arabic: ['Alexandria', 'sans-serif'],
    },
  },

  'minimal-scandinavian': {
    layoutVariant: 'minimal',
    colors: {
      // "Near-monochrome, one warm accent, huge whitespace. Nothing competes with the
      // research itself." -- clay is the single accent everywhere, reused for citation
      // markers too (no second accent color per the "nothing competes" ethos).
      espresso: { DEFAULT: '#1A1A1A', 2: '#404040' },
      brick: { DEFAULT: '#B5654A', dark: '#96513A', light: '#F5E8E3' },
      vellum: { DEFAULT: '#FFFFFF', 2: '#FAFAFA' },
      ink: { DEFAULT: '#333333', 2: '#6B6B6B', 3: '#B0B0B0' },
      dust: '#E5E5E5',
      moss: OPEN_ACCESS_GREEN,
      citation: '#B5654A',
      mint: '#9CA3A0',
    },
    fontFamily: {
      display: ['"Work Sans"', '"Helvetica Neue"', 'Arial', 'sans-serif'],
      prose: ['"Work Sans"', '"Helvetica Neue"', 'Arial', 'sans-serif'],
      mono: ['"Roboto Mono"', '"Courier New"', 'monospace'],
      sans: ['"Work Sans"', '"Helvetica Neue"', 'Arial', 'sans-serif'],
      arabic: ['Alexandria', 'sans-serif'],
    },
  },

  'field-notes': {
    layoutVariant: 'field',
    colors: {
      // "Forest green and parchment, gold accent. Bold banner moments, organic rather than
      // clinical." -- sage doubles as the citation/footnote-marker accent.
      espresso: { DEFAULT: '#1F3D2E', 2: '#2A4F3B' },
      brick: { DEFAULT: '#B8862E', dark: '#9A6F22', light: '#F3E8D2' },
      vellum: { DEFAULT: '#F5F0E4', 2: '#EDE6D3' },
      ink: { DEFAULT: '#2A3B30', 2: '#5C6B5F', 3: '#9CA89C' },
      dust: '#D9D2BE',
      moss: OPEN_ACCESS_GREEN,
      citation: '#7C9473',
      mint: '#D4C48A',
    },
    fontFamily: {
      display: ['Lora', 'Georgia', 'serif'],
      prose: ['Karla', 'system-ui', 'sans-serif'],
      mono: ['"DM Mono"', '"Courier New"', 'monospace'],
      sans: ['Karla', 'system-ui', 'sans-serif'],
      arabic: ['Alexandria', 'sans-serif'],
    },
  },
};
